/** Service Desk console: the CSV import wizard in Service settings (C22, SD-S1-50): upload, map columns, validate (dry run), done. */
export const serviceImport = {
  nav: "Import",
  title: "Import",
  subtitle: "Bring organisations and contacts in from a spreadsheet. We check every row before anything is saved.",
  stepsLabel: "Import steps",
  stepCompleted: "(completed)",
  stepHeading: "Step {{n}} of 4 · {{name}}",
  steps: { upload: "Upload", map: "Map columns", validate: "Validate", done: "Done" },
  kinds: { organisations: "Organisations", contacts: "Contacts", tickets: "Historical tickets", sites: "Sites", assets: "Assets", products: "Products" },
  kindHint: {
    organisations: "Matched by external id, display code, then name",
    contacts: "Matched by e-mail",
    tickets: "Old tickets, matched by the id they had in the old system",
    sites: "Client sites from a CSV file",
    assets: "Installed equipment from a CSV file",
    products: "Catalogue products from a CSV file",
  },
  kindGroup: "What are you importing?",
  upload: {
    dropTitle: "Drop a CSV file here",
    dropActive: "Release to upload",
    dropHint: "UTF-8, up to {{size}} MB and {{rows}} rows. The first row holds the column names.",
    choose: "Choose a CSV file",
    chooseLabel: "Choose a CSV file for {{kind}}",
    reading: "Reading {{name}}…",
    fileDetails: "{{rows}} rows · {{columns}} columns · {{size}} · UTF-8",
    remove: "Remove file",
    removed: "File removed. Nothing was imported.",
    template: "Download the {{kind}} template",
    templateDone: "Template downloaded",
    columns: "Columns: {{columns}}",
    noteTitle: "Nothing is written yet.",
    noteBody: "Step 3 is a dry run that checks every row. Rows that match an existing record update it; the rest are created.",
    rejected: {
      type: "“{{name}}” is not a CSV file. Save your spreadsheet as CSV (UTF-8) and try again.",
      size: "“{{name}}” is too large. The limit is {{size}} MB.",
      empty: "“{{name}}” is empty.",
      count: "Choose one file at a time.",
    },
  },
  fields: {
    organisations: {
      name: "Name",
      display_code: "Code",
      kind: "Kind",
      industry: "Industry",
      tier: "Tier",
      email_domains: "E-mail domains",
      tin: "Tax ID",
      external_id: "External ID",
      external_source: "External ID source",
      account_manager_email: "Account manager e-mail",
      billing_address: "Billing address",
      shipping_address: "Shipping address",
    },
    contacts: {
      name: "Name",
      email: "E-mail",
      mobile: "Mobile",
      job_title: "Job title",
      organisation: "Organisation",
      org_role: "Organisation role",
      visibility: "Ticket visibility",
      can_order: "Can order",
      order_limit_php: "Order limit (PHP)",
      can_approve_orders: "Can approve orders",
      language: "Language",
    },
    tickets: {
      external_id: "Ticket ID in the old system",
      external_source: "Old system name",
      title: "Title",
      description: "Description",
      status: "Status",
      priority: "Priority",
      type: "Ticket type",
      created_at: "Created",
      updated_at: "Last updated",
      resolved_at: "Resolved",
      closed_at: "Closed",
      requester_email: "Requester e-mail",
      requester_external_id: "Requester ID in the old system",
      organisation: "Organisation",
      assignee_email: "Assignee e-mail",
      tags: "Tags",
      resolution_note: "Resolution note",
      messages: "Messages (JSON)",
      sla_timers: "Start SLA timers",
    },
  },
  map: {
    intro: "We matched the column names where we could. Check each one; required fields are marked with *.",
    caption: "Column mapping",
    columnInFile: "Column in your file",
    firstRows: "First rows",
    goesInto: "Goes into",
    ignore: "Ignore this column",
    fieldFor: "Field for column {{column}}",
    missing: "Map the required fields: {{fields}}.",
    duplicate: "{{fields}} is mapped from more than one column. Pick one.",
    remember: "Remember this mapping for the next file",
    empty: "(empty)",
  },
  validate: {
    checking: "Checking every row…",
    checkingHint: "This takes a moment for a large file. Nothing is saved yet.",
    summary: "Dry-run result",
    rows: "Rows in the file",
    rowsSub: "in the file",
    create: "Will be created",
    createSub: "new records",
    update: "Will update",
    updateSub: "existing records",
    unchanged: "Already up to date",
    unchangedSub: "nothing to change",
    errors: "Row errors",
    errorsNone: "none",
    errorsSome: "skipped unless fixed",
    errorsTitle_one: "{{count}} row error",
    errorsTitle_other: "{{count}} row errors",
    errorsCaption: "Row errors",
    showing: "Showing the first {{shown}} errors. Download the file for the full list.",
    row: "Row",
    rowN: "Row {{row}}",
    column: "Column",
    value: "Value",
    problem: "Problem",
    emptyValue: "(empty)",
    download: "Download errors (CSV)",
    downloaded: "Row errors downloaded",
    allPassed: "Every row passed. You can import now.",
    nothingChanges: "Nothing would change: every row already matches a record. You can start over with another file.",
    skip_one: "Import the valid rows ({{valid}}) and skip the row with an error. Download the error file to fix it and upload it later.",
    skip_other: "Import the valid rows ({{valid}}) and skip the {{count}} rows with errors. Download the error file to fix and upload them later.",
  },
  done: {
    runningTitle: "Import {{file}}",
    running: "Running",
    progressLabel: "Import progress",
    progressText: "{{done}} of {{total}} rows",
    completed: "Completed",
    failed: "Import failed",
    cancelled: "Import cancelled",
    report: "Import report",
    created: "Created",
    createdSub: "new records",
    updated: "Updated",
    updatedSub: "existing records",
    unchanged: "Unchanged",
    unchangedSub: "already up to date",
    skipped: "Skipped",
    skippedSub: "rows with errors",
    duration: "Duration",
    durationValue: "{{seconds}} s",
    audit: "Recorded in the audit log by import job {{id}}.",
    verifyNote: "Imported contacts are not verified: they verify by e-mail or magic link the first time they use the portal.",
    openOrganisations: "Open organisations",
    openContacts: "Open contacts",
    downloadSkipped_one: "Download the skipped row",
    downloadSkipped_other: "Download the {{count}} skipped rows",
    another: "Import another file",
    failedTitle: "The import did not finish",
    failedBody: "Rows saved before it stopped are kept. Running the same file again is safe: rows that already match are left alone.",
    cancelledTitle: "This import was cancelled",
    cancelledBody: "The uploaded file was deleted. Rows an import had already saved are kept.",
    startOver: "Start a new import",
    empty: { title: "No import has run yet", body: "Start with a CSV file, check it in the dry run, then import." },
  },
  actions: {
    back: "Back",
    nextMap: "Next: Map columns",
    nextValidate: "Next: Validate",
    import_one: "Import {{count}} row",
    import_other: "Import {{count}} rows",
    footer: "Import actions",
  },
  states: {
    loadTitle: "The import service could not load",
    loadBody: "Nothing was changed. Check your connection and try again.",
    retry: "Try again",
    noAccessTitle: "You cannot import records",
    noAccessBody: "Importing needs the permission to manage organisations. Ask a workspace admin to add it to your role.",
    failedStep: "That step did not go through. Nothing was changed.",
  },
  rejections: {
    not_utf8: "The file is not UTF-8 (some characters are garbled). Export it again as CSV (UTF-8).",
    not_csv: "That does not look like a CSV file. Save your spreadsheet as CSV (UTF-8).",
    empty_file: "The file is empty.",
    no_rows: "The file has no data rows. The first row must hold the column names, followed by at least one row.",
    too_many_rows: "The file has more than {{rows}} rows. The limit is {{rows}} per file.",
    too_many_columns: "The file has more than {{columns}} columns.",
    cell_too_large: "A cell in the file is far too large to be a real value.",
    file_too_large: "The file is larger than {{size}} MB. The limit is {{size}} MB.",
    kind_unavailable: "This kind of import is not available for your workspace.",
    mapping_unknown_column: "The mapping names a column that is not in the file.",
    mapping_unknown_field: "The mapping names a field that does not exist.",
    mapping_duplicate_field: "More than one column is mapped to the same field.",
    mapping_missing_required: "Map every required field before checking the file.",
    import_not_ready: "This import is not ready for that step. Reload the page to see where it is.",
    import_has_errors: "The file has row errors. Fix them, or choose to skip the rows with errors.",
    import_nothing_to_import: "There are no valid rows to import.",
    import_ended: "This import has already ended.",
    import_expired: "The uploaded file is no longer stored. Upload it again.",
    import_no_errors: "This import has no row errors.",
  },
} as const;
